import {
	AlertDialog,
	AlertDialogAction,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
	Dialog,
	DialogContent,
	DialogFooter,
	DialogHeader,
	DialogTitle,
	DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { CatalogService } from "@/gen/omnios/v1/catalog_connect";
import { transport } from "@/lib/grpc";
import { useBreadcrumbs } from "@/lib/use-breadcrumbs";
import { createClient } from "@connectrpc/connect";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { AlertCircle, Layers, Loader2, Plus, Trash2 } from "lucide-react";
import { useState } from "react";

export const Route = createFileRoute("/settings/catalog/groups")({
	component: PropertyGroupsPage,
});

const client = createClient(CatalogService, transport);

function slugify(name: string): string {
	// Transliterate common Cyrillic to Latin for slug
	const map: Record<string, string> = {
		а: "a",
		б: "b",
		в: "v",
		г: "g",
		д: "d",
		е: "e",
		ё: "yo",
		ж: "zh",
		з: "z",
		и: "i",
		й: "y",
		к: "k",
		л: "l",
		м: "m",
		н: "n",
		о: "o",
		п: "p",
		р: "r",
		с: "s",
		т: "t",
		у: "u",
		ф: "f",
		х: "kh",
		ц: "ts",
		ч: "ch",
		ш: "sh",
		щ: "shch",
		ъ: "",
		ы: "y",
		ь: "",
		э: "e",
		ю: "yu",
		я: "ya",
	};
	return name
		.toLowerCase()
		.split("")
		.map((c) => map[c] ?? c)
		.join("")
		.replace(/[^a-z0-9]+/g, "_")
		.replace(/^_|_$/g, "");
}

function PropertyGroupsPage() {
	useBreadcrumbs([
		{ label: "Настройки", href: "/settings" },
		{ label: "Каталог", href: "/settings/catalog" },
		{ label: "Группы атрибутов" },
	]);

	const qc = useQueryClient();
	const [showCreate, setShowCreate] = useState(false);
	const [newName, setNewName] = useState("");
	const [newSlug, setNewSlug] = useState("");
	const [deleteId, setDeleteId] = useState<string | null>(null);

	const { data, isLoading, error } = useQuery({
		queryKey: ["property-groups"],
		queryFn: () => client.listPropertyGroups({}),
	});
	const groups = data?.groups ?? [];

	const createMut = useMutation({
		mutationFn: () =>
			client.createPropertyGroup({
				name: newName.trim(),
				slug: newSlug.trim(),
				sortOrder: groups.length,
			}),
		onSuccess: () => {
			qc.invalidateQueries({ queryKey: ["property-groups"] });
			setShowCreate(false);
			setNewName("");
			setNewSlug("");
		},
	});

	const deleteMut = useMutation({
		mutationFn: (id: string) => client.deletePropertyGroup({ id }),
		onSuccess: () => {
			qc.invalidateQueries({ queryKey: ["property-groups"] });
			setDeleteId(null);
		},
	});

	if (error) {
		return (
			<Card className="flex flex-col items-center py-12 gap-3">
				<AlertCircle className="h-10 w-10 text-destructive" />
				<p className="text-sm text-muted-foreground">
					Не удалось загрузить группы: {error.message}
				</p>
			</Card>
		);
	}

	return (
		<div className="space-y-6">
			<div className="flex items-center justify-between">
				<div>
					<h2 className="text-lg font-semibold">Группы атрибутов</h2>
					<p className="text-sm text-muted-foreground">
						Организуйте свойства по группам для удобной навигации
					</p>
				</div>
				<Dialog
					open={showCreate}
					onOpenChange={(open) => {
						setShowCreate(open);
						if (!open) {
							setNewName("");
							setNewSlug("");
						}
					}}
				>
					<DialogTrigger asChild>
						<Button size="sm">
							<Plus className="h-4 w-4 mr-1" />
							Добавить группу
						</Button>
					</DialogTrigger>
					<DialogContent>
						<DialogHeader>
							<DialogTitle>Новая группа атрибутов</DialogTitle>
						</DialogHeader>
						<div className="space-y-4">
							<div className="space-y-2">
								<Label>Название</Label>
								<Input
									value={newName}
									onChange={(e) => {
										setNewName(e.target.value);
										setNewSlug(slugify(e.target.value));
									}}
									placeholder="Технические характеристики"
								/>
							</div>
							<div className="space-y-1">
								<Label>Идентификатор</Label>
								<Input
									value={newSlug}
									onChange={(e) => setNewSlug(e.target.value)}
									placeholder="technical"
								/>
								<p className="text-xs text-muted-foreground">
									Используется в API. Только латиница, цифры, подчёркивания.
								</p>
							</div>
						</div>
						<DialogFooter>
							<Button
								disabled={
									!newName.trim() || !newSlug.trim() || createMut.isPending
								}
								onClick={() => createMut.mutate()}
							>
								{createMut.isPending && (
									<Loader2 className="h-4 w-4 animate-spin mr-1" />
								)}
								Создать
							</Button>
						</DialogFooter>
					</DialogContent>
				</Dialog>
			</div>

			{isLoading && (
				<div className="flex justify-center py-12">
					<Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
				</div>
			)}

			{!isLoading && groups.length === 0 && (
				<Card className="flex flex-col items-center py-12 gap-3">
					<Layers className="h-10 w-10 text-muted-foreground" />
					<p className="text-sm text-muted-foreground">
						Нет групп атрибутов. Создайте первую для структурирования свойств.
					</p>
				</Card>
			)}

			{!isLoading && groups.length > 0 && (
				<div className="space-y-2">
					{groups.map((g) => (
						<Card key={g.id} className="px-4 py-3 flex items-center gap-3">
							<div className="flex-1">
								<span className="text-sm font-medium">{g.name}</span>
								<span className="text-xs text-muted-foreground ml-2 font-mono">
									{g.slug}
								</span>
								{g.description && (
									<p className="text-xs text-muted-foreground mt-0.5">
										{g.description}
									</p>
								)}
							</div>
							<Button
								variant="ghost"
								size="sm"
								className="text-destructive"
								onClick={() => setDeleteId(g.id)}
							>
								<Trash2 className="h-4 w-4" />
							</Button>
						</Card>
					))}
				</div>
			)}

			{/* Delete confirmation */}
			<AlertDialog
				open={!!deleteId}
				onOpenChange={(open) => !open && setDeleteId(null)}
			>
				<AlertDialogContent>
					<AlertDialogHeader>
						<AlertDialogTitle>Удалить группу?</AlertDialogTitle>
						<AlertDialogDescription>
							Свойства, привязанные к этой группе, потеряют группировку. Это
							действие нельзя отменить.
						</AlertDialogDescription>
					</AlertDialogHeader>
					<AlertDialogFooter>
						<AlertDialogCancel>Отмена</AlertDialogCancel>
						<AlertDialogAction
							onClick={() => deleteId && deleteMut.mutate(deleteId)}
							className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
						>
							Удалить
						</AlertDialogAction>
					</AlertDialogFooter>
				</AlertDialogContent>
			</AlertDialog>
		</div>
	);
}
